import { createContext, useContext, useState, useEffect } from "react"
import { api } from '../api';

// Create the context
const AuthContext = createContext();

// Create a custom hook so the itinerary components can access data w/out the risk of 'undefined' error crash
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within a AuthProvider");
    }
    return context;
};

// Create the Provider component which will wrap our page
export const AuthProvider = ({ children }) => {

    // Initialize current user
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    // Persist login on page reload
    useEffect(() => {
        const verifyUser = async () => {
            // Check if the token is inside a URL or not
            const urlToken = new URLSearchParams(window.location.search).get('token');
            if (urlToken) {
                const next = new URLSearchParams(window.location.search).get('next');
                localStorage.setItem('token', urlToken);
                // Redirect to intended destination if provided, otherwise clean the URL
                if (next) {
                    window.location.href = next;
                    return;
                }
                window.history.replaceState({}, document.title, window.location.pathname);
            }
            // Get the token
            const token = localStorage.getItem('token');
            if (!token) {
                setIsAuthLoading(false);
                return;
            }
            try {
                // Fetch the user data using our JWT token
                const data = await api.get('/auth/me');
                setCurrentUser(data.user);
            } catch (err) {
                console.error("Auth verification failed", err);
                localStorage.removeItem('token');
            } finally {
                setIsAuthLoading(false);
            }
        };
        verifyUser();
    }, []);

    const login = (token, userData) => {
        // Save token to browser
        localStorage.setItem('token', token);
        // Save user to state
        setCurrentUser(userData);
    };

    const logout = () => {
        // Remove token from browser
        localStorage.removeItem('token');
        // Remove user from state
        setCurrentUser(null);
    }

    const updateUser = (userData) => {
        setCurrentUser(userData);
    };

    // Bundle everything we want to share into the "value" object
    const value = {
        currentUser, login, logout, updateUser
    };

    // Show a loading screen while we verify the token so ProtectedRoutes don't flash
    if (isAuthLoading) {
        return <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>Verifying Session...</div>;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
};